import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, Shield, Zap, Globe, ChevronDown, ChevronRight, 
  CreditCard, RefreshCw, Smartphone, MessageCircle, FileText,
  Award, Lock, Users, TrendingUp, CheckCircle, ArrowRight,
  Menu, X, ExternalLink, Send, Phone, Mail, MapPin, Check
} from 'lucide-react';
import { useLanguage } from './i18n';

// Colors
const COLORS = {
  primary: '#22c55e',
  primaryDark: '#16a34a',
  dark: '#0f172a',
  darkLight: '#1e293b',
  gray: '#64748b',
  grayLight: '#94a3b8',
  white: '#ffffff',
  bg: '#f8fafc'
};

// API URL
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LandingPage = () => {
  const { t, language, setLanguage, currentLanguage, supportedLanguages } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [rates, setRates] = useState(null);
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  // Fetch rates and update every 5 minutes
  useEffect(() => {
    const fetchRates = async () => {
      try {
        // Fetch all rates including MDL/USD
        const [generalRates, mdlRates, mdlUsdRate] = await Promise.all([
          fetch(`${API_URL}/api/exchange/rates`).then(r => r.json()),
          fetch(`${API_URL}/api/exchange/mdl-rates`).then(r => r.json()),
          fetch(`${API_URL}/api/exchange/mdl-usd`).then(r => r.json())
        ]);
        
        if (generalRates.success) {
          const ratesData = generalRates.rates || {};
          const markup = 1.005; // 0.5% наценка
          
          // USD/KGS - доллар к сому
          const usdKgs = ratesData.USD && ratesData.KGS 
            ? ((ratesData.USD / ratesData.KGS) * markup).toFixed(2) 
            : '87.94';
          
          // MDL/EUR - лей к евро
          const mdlEur = mdlRates?.rates?.EUR 
            ? ((1 / mdlRates.rates.EUR.sell) * markup).toFixed(4) 
            : '0.0497';
          
          // KZT/USD - тенге к доллару
          const kztUsd = ratesData.KZT && ratesData.USD 
            ? ((ratesData.KZT / ratesData.USD) * markup).toFixed(4) 
            : '0.0020';
          
          // MDL/USD - молдавский лей к доллару (официальный курс BNM + 0.5%)
          const mdlUsd = mdlUsdRate?.success && mdlUsdRate?.rate
            ? (mdlUsdRate.rate * markup).toFixed(4)
            : '16.9860';
          
          setRates({
            usdKgs,
            mdlEur,
            kztUsd,
            mdlUsd,
            updatedAt: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
          });
        }
      } catch (error) {
        console.error('Error fetching rates:', error);
        // Fallback rates (with 0.5% markup)
        setRates({
          usdKgs: '87.94',
          mdlEur: '0.0497',
          kztUsd: '0.0020',
          mdlUsd: '16.9860',
          updatedAt: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
        });
      }
    };
    
    fetchRates();
    
    // Update every 5 minutes
    const interval = setInterval(fetchRates, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Language Modal */}
      <AnimatePresence>
        {showLanguageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowLanguageModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-xs rounded-2xl overflow-hidden mx-4 bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-[16px] font-semibold text-center" style={{ color: COLORS.dark }}>{t('profile.language.select')}</h2>
              </div>
              <div className="p-3 space-y-2">
                {supportedLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setShowLanguageModal(false);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl transition"
                    style={{ background: language === lang.code ? '#dcfce7' : '#f1f5f9' }}
                    data-testid={`landing-language-${lang.code}`}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <span className="flex-1 text-left font-medium" style={{ color: COLORS.dark }}>{lang.name}</span>
                    {language === lang.code && <Check className="w-5 h-5" style={{ color: COLORS.primary }} />}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src="/polytech-logo.png" alt="PolyTech Wallet" className="w-10 h-10 rounded-xl" />
              <span className="font-bold text-xl" style={{ color: COLORS.dark }}>PolyTech Wallet</span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollTo('features')} className="text-sm font-medium hover:text-green-600 transition" style={{ color: COLORS.gray }}>
                {t('landing.features')}
              </button>
              <button onClick={() => scrollTo('security')} className="text-sm font-medium hover:text-green-600 transition" style={{ color: COLORS.gray }}>
                {t('landing.security')}
              </button>
              <button onClick={() => scrollTo('faq')} className="text-sm font-medium hover:text-green-600 transition" style={{ color: COLORS.gray }}>
                {t('landing.faq')}
              </button>
              <button onClick={() => scrollTo('documents')} className="text-sm font-medium hover:text-green-600 transition" style={{ color: COLORS.gray }}>
                {t('landing.documents')}
              </button>
              <button onClick={() => scrollTo('contacts')} className="text-sm font-medium hover:text-green-600 transition" style={{ color: COLORS.gray }}>
                {t('landing.contacts')}
              </button>
              {/* Language selector */}
              <button 
                onClick={() => setShowLanguageModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition"
                data-testid="landing-language-btn"
              >
                <span className="text-lg">{currentLanguage?.flag}</span>
                <ChevronDown size={14} style={{ color: COLORS.gray }} />
              </button>
            </nav>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              <a 
                href="https://t.me/polytechex_bot" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-xl font-medium text-white text-sm transition hover:opacity-90"
                style={{ background: COLORS.primary }}
              >
                {t('landing.openWallet')}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-gray-100"
            >
              <div className="px-4 py-4 space-y-3">
                <button onClick={() => scrollTo('features')} className="block w-full text-left py-2 text-sm font-medium" style={{ color: COLORS.gray }}>
                  {t('landing.features')}
                </button>
                <button onClick={() => scrollTo('security')} className="block w-full text-left py-2 text-sm font-medium" style={{ color: COLORS.gray }}>
                  {t('landing.security')}
                </button>
                <button onClick={() => scrollTo('faq')} className="block w-full text-left py-2 text-sm font-medium" style={{ color: COLORS.gray }}>
                  {t('landing.faq')}
                </button>
                <button onClick={() => scrollTo('documents')} className="block w-full text-left py-2 text-sm font-medium" style={{ color: COLORS.gray }}>
                  {t('landing.documents')}
                </button>
                <button onClick={() => scrollTo('contacts')} className="block w-full text-left py-2 text-sm font-medium" style={{ color: COLORS.gray }}>
                  {t('landing.contacts')}
                </button>
                {/* Language selector mobile */}
                <button 
                  onClick={() => setShowLanguageModal(true)}
                  className="flex items-center gap-2 py-2"
                  style={{ color: COLORS.gray }}
                >
                  <span className="text-lg">{currentLanguage?.flag}</span>
                  <span className="text-sm font-medium">{currentLanguage?.name}</span>
                </button>
                <a 
                  href="https://t.me/polytechex_bot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block w-full text-center px-5 py-3 rounded-xl font-medium text-white text-sm mt-4"
                  style={{ background: COLORS.primary }}
                >
                  {t('landing.openWallet')}
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24" style={{ background: `linear-gradient(135deg, ${COLORS.dark} 0%, ${COLORS.darkLight} 100%)` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 mb-6">
                <Award size={16} style={{ color: COLORS.primary }} />
                <span className="text-sm font-medium" style={{ color: COLORS.primary }}>
                  {t('landing.licensedBroker')}
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {t('landing.heroTitle')} <br />
                <span style={{ color: COLORS.primary }}>{t('landing.heroSubtitle')}</span>
              </h1>
              
              <p className="text-lg mb-8" style={{ color: COLORS.grayLight }}>
                {t('landing.heroDescription')}
              </p>

              {rates && (
                <div className="flex flex-wrap gap-3 mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                    <TrendingUp size={16} style={{ color: COLORS.primary }} />
                    <span className="text-white text-sm font-medium">
                      MDL/USD: <span style={{ color: COLORS.primary }}>{rates.mdlUsd}</span>
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                    <TrendingUp size={16} style={{ color: COLORS.primary }} />
                    <span className="text-white text-sm font-medium">
                      USD/KGS: <span style={{ color: COLORS.primary }}>{rates.usdKgs}</span>
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                    <TrendingUp size={16} style={{ color: COLORS.primary }} />
                    <span className="text-white text-sm font-medium">
                      MDL/EUR: <span style={{ color: COLORS.primary }}>{rates.mdlEur}</span>
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                    <TrendingUp size={16} style={{ color: COLORS.primary }} />
                    <span className="text-white text-sm font-medium">
                      KZT/USD: <span style={{ color: COLORS.primary }}>{rates.kztUsd}</span>
                    </span>
                  </div>
                  <div className="text-xs mt-1 w-full" style={{ color: COLORS.grayLight }}>
                    {t('general.updated')}: {rates.updatedAt}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://t.me/polytechex_bot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-semibold text-white text-lg transition hover:opacity-90"
                  style={{ background: COLORS.primary }}
                >
                  <Send size={20} />
                  Telegram Bot
                </a>
                <a 
                  href="/wallet" 
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-semibold text-lg transition border-2"
                  style={{ borderColor: COLORS.primary, color: COLORS.primary }}
                >
                  <Globe size={20} />
                  {t('landing.webVersion')}
                </a>
              </div>
            </div>

            <div className="relative hidden md:block">
              <div className="relative z-10">
                <img 
                  src="/polytech-logo.png" 
                  alt="PolyTech Wallet App" 
                  className="w-64 h-64 mx-auto rounded-3xl shadow-2xl"
                  style={{ background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)` }}
                />
              </div>
              <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-20 blur-3xl"
                style={{ background: COLORS.primary }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: language === 'en' ? '5-8 sec' : '5-8 сек', labelKey: 'landing.exchangeTime' },
              { value: '1.7%', labelKey: 'landing.commission' },
              { value: '24/7', labelKey: 'landing.support247' },
              { value: '100%', labelKey: 'landing.legal' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl md:text-4xl font-bold mb-2" style={{ color: COLORS.primary }}>{stat.value}</p>
                <p className="text-sm" style={{ color: COLORS.gray }}>{t(stat.labelKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24" style={{ background: COLORS.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
              {t('landing.allFunctionsInOne')}
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.gray }}>
              {t('landing.buyAndSell')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <CreditCard size={28} />,
                titleKey: 'landing.cardTopup',
                descKey: 'landing.cardTopupDesc'
              },
              {
                icon: <RefreshCw size={28} />,
                titleKey: 'landing.onlineExchange',
                descKey: 'landing.onlineExchangeDesc'
              },
              {
                icon: <Wallet size={28} />,
                titleKey: 'landing.cardWithdrawal',
                descKey: 'landing.cardWithdrawalDesc'
              },
              {
                icon: <Smartphone size={28} />,
                titleKey: 'landing.mobilePayments',
                descKey: 'landing.mobilePaymentsDesc'
              },
              {
                icon: <Shield size={28} />,
                titleKey: 'landing.kycVerification',
                descKey: 'landing.kycVerificationDesc'
              },
              {
                icon: <Users size={28} />,
                titleKey: 'landing.referralProgram',
                descKey: 'landing.referralProgramDesc'
              }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
                >
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: COLORS.dark }}>{t(feature.titleKey)}</h3>
                <p className="text-sm" style={{ color: COLORS.gray }}>{t(feature.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
              {t('landing.howItWorks')}
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.gray }}>
              {t('landing.threeSimpleSteps')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                titleKey: 'landing.step1Title',
                descKey: 'landing.step1Desc'
              },
              {
                step: '2',
                titleKey: 'landing.step2Title',
                descKey: 'landing.step2Desc'
              },
              {
                step: '3',
                titleKey: 'landing.step3Title',
                descKey: 'landing.step3Desc'
              }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white"
                  style={{ background: COLORS.primary }}
                >
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: COLORS.dark }}>{t(item.titleKey)}</h3>
                <p style={{ color: COLORS.gray }}>{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-16 md:py-24" style={{ background: COLORS.dark }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              {t('landing.securityFirst')}
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.grayLight }}>
              {t('landing.modernTech')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Lock size={24} />, titleKey: 'landing.pinProtection', descKey: 'landing.pinProtectionDesc' },
              { icon: <Shield size={24} />, titleKey: 'landing.kycSecurity', descKey: 'landing.kycSecurityDesc' },
              { icon: <CheckCircle size={24} />, titleKey: 'landing.amlSecurity', descKey: 'landing.amlSecurityDesc' },
              { icon: <FileText size={24} />, titleKey: 'landing.licenseSecurity', descKey: 'landing.licenseSecurityDesc' }
            ].map((item, i) => (
              <div key={i} className="text-center p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: `${COLORS.primary}20`, color: COLORS.primary }}
                >
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-white">{t(item.titleKey)}</h3>
                <p className="text-sm" style={{ color: COLORS.grayLight }}>{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
              {t('landing.frequentQuestions')}
            </h2>
          </div>

          <div className="space-y-4">
            {[
              { qKey: 'faq.q1', aKey: 'faq.a1' },
              { qKey: 'faq.q2', aKey: 'faq.a2' },
              { qKey: 'faq.q3', aKey: 'faq.a3' },
              { qKey: 'faq.q4', aKey: 'faq.a4' },
              { qKey: 'faq.q5', aKey: 'faq.a5' },
              { qKey: 'faq.q6', aKey: 'faq.a6' }
            ].map((item, i) => (
              <div 
                key={i} 
                className="border rounded-2xl overflow-hidden"
                style={{ borderColor: openFaq === i ? COLORS.primary : '#e5e7eb' }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-medium" style={{ color: COLORS.dark }}>{t(item.qKey)}</span>
                  <ChevronDown 
                    size={20} 
                    className={`transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                    style={{ color: COLORS.gray }}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5" style={{ color: COLORS.gray }}>{t(item.aKey)}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-16 md:py-24" style={{ background: COLORS.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
              {t('landing.legalDocuments')}
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.gray }}>
              {t('landing.workingLegally')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { titleKey: 'docs.license.title', descKey: 'docs.license.desc', icon: <Award size={24} /> },
              { titleKey: 'docs.registration.title', descKey: 'docs.registration.desc', icon: <FileText size={24} /> },
              { titleKey: 'docs.amlPolicy.title', descKey: 'docs.amlPolicy.desc', icon: <Shield size={24} /> },
              { titleKey: 'docs.userAgreement.title', descKey: 'docs.userAgreement.desc', icon: <FileText size={24} /> },
              { titleKey: 'docs.privacyPolicy.title', descKey: 'docs.privacyPolicy.desc', icon: <Lock size={24} /> },
              { titleKey: 'docs.registryExtract.title', descKey: 'docs.registryExtract.desc', icon: <FileText size={24} /> }
            ].map((doc, i) => (
              <motion.a
                key={i}
                href="/documents"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition block"
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
                >
                  {doc.icon}
                </div>
                <h3 className="font-semibold mb-2" style={{ color: COLORS.dark }}>{t(doc.titleKey)}</h3>
                <p className="text-sm" style={{ color: COLORS.gray }}>{t(doc.descKey)}</p>
              </motion.a>
            ))}
          </div>

          <div className="text-center mt-8">
            <a 
              href="/documents"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition hover:opacity-90"
              style={{ background: COLORS.primary, color: COLORS.white }}
            >
              <FileText size={20} />
              {t('landing.allDocuments')}
            </a>
          </div>

          <div className="mt-12 p-6 rounded-2xl text-center" style={{ background: `${COLORS.primary}10` }}>
            <p className="text-sm" style={{ color: COLORS.dark }}>
              <strong>{t('docs.companyInfo')}</strong>
              <br />
              {t('docs.licenseNumbers')} | ИНН: 01111201510129 | Тел: +996 507 210 992
            </p>
          </div>
        </div>
      </section>

      {/* Contacts Section */}
      <section id="contacts" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
              {t('landing.contactsTitle')}
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.gray }}>
              {t('landing.contactUs')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl" style={{ background: COLORS.bg }}>
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
              >
                <MessageCircle size={28} />
              </div>
              <h3 className="font-semibold mb-2" style={{ color: COLORS.dark }}>{t('landing.telegramSupport')}</h3>
              <a href="https://t.me/PolyTechSupport" className="hover:underline" style={{ color: COLORS.primary }}>
                @PolyTechSupport
              </a>
            </div>

            <div className="text-center p-8 rounded-2xl" style={{ background: COLORS.bg }}>
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
              >
                <Mail size={28} />
              </div>
              <h3 className="font-semibold mb-2" style={{ color: COLORS.dark }}>Email</h3>
              <a href="mailto:support@polytechex.com" className="hover:underline" style={{ color: COLORS.primary }}>
                support@polytechex.com
              </a>
            </div>

            <div className="text-center p-8 rounded-2xl" style={{ background: COLORS.bg }}>
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
              >
                <MapPin size={28} />
              </div>
              <h3 className="font-semibold mb-2" style={{ color: COLORS.dark }}>{t('landing.address')}</h3>
              <p style={{ color: COLORS.gray }}>
                г. Бишкек, ул. Токтогула, 87A
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24" style={{ background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            {t('landing.startExchangingToday')}
          </h2>
          <p className="text-lg mb-8 text-white/80">
            {t('landing.joinUsers')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://t.me/polytechex_bot" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-semibold text-lg transition bg-white hover:bg-gray-100"
              style={{ color: COLORS.primary }}
            >
              <Send size={20} />
              {t('landing.openInTelegram')}
            </a>
            <a 
              href="/wallet" 
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-semibold text-lg transition border-2 border-white text-white hover:bg-white/10"
            >
              <Globe size={20} />
              {t('landing.webVersion')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: COLORS.dark }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/polytech-logo.png" alt="PolyTech Wallet" className="w-10 h-10 rounded-xl" />
                <span className="font-bold text-xl text-white">PolyTech Wallet</span>
              </div>
              <p className="text-sm mb-4" style={{ color: COLORS.grayLight }}>
                {t('landing.licensedBroker')}
              </p>
              {/* Payment Systems Logos */}
              <div className="flex flex-wrap gap-2">
                <div className="px-2 py-1 rounded bg-white/10 text-xs font-bold text-green-400">МИР</div>
                <div className="px-2 py-1 rounded bg-white/10 text-xs font-bold text-purple-400">СБП</div>
                <div className="px-2 py-1 rounded bg-white/10 text-xs font-bold text-blue-400">VISA</div>
                <div className="px-2 py-1 rounded bg-white/10 text-xs font-bold text-orange-400">MC</div>
                <div className="px-2 py-1 rounded bg-white/10 text-xs font-bold text-green-300">MIA</div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">{t('landing.wallet')}</h4>
              <ul className="space-y-2">
                <li><a href="https://t.me/polytechex_bot" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>Telegram Bot</a></li>
                <li><a href="/wallet" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>{t('landing.webVersion')}</a></li>
                <li><a href="#features" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>{t('landing.features')}</a></li>
                <li><a href="/payment-methods" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>Способы оплаты</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">{t('landing.company')}</h4>
              <ul className="space-y-2">
                <li><a href="/about" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>О компании</a></li>
                <li><a href="/documents" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>{t('landing.documents')}</a></li>
                <li><a href="/documents" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>{t('legal.aml')}</a></li>
                <li><a href="/documents" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>{t('legal.terms')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">{t('landing.supportFooter')}</h4>
              <ul className="space-y-2">
                <li><a href="https://t.me/PolyTechSupport" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>{t('landing.telegramSupport')}</a></li>
                <li><a href="mailto:support@polytechex.com" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>Email</a></li>
                <li><a href="#contacts" className="text-sm hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>{t('landing.contacts')}</a></li>
              </ul>
            </div>
          </div>

          {/* Payment Systems Row */}
          <div className="border-t border-gray-800 mt-8 pt-8">
            <p className="text-center text-sm mb-4" style={{ color: COLORS.grayLight }}>Принимаем к оплате:</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <div className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5">
                <svg viewBox="0 0 60 24" className="h-5">
                  <rect fill="#4DB45E" width="60" height="24" rx="3"/>
                  <text x="30" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">МИР</text>
                </svg>
              </div>
              <div className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5">
                <svg viewBox="0 0 60 24" className="h-5">
                  <rect fill="#5C2D91" width="60" height="24" rx="3"/>
                  <text x="30" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">СБП</text>
                </svg>
              </div>
              <div className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5">
                <svg viewBox="0 0 60 24" className="h-5">
                  <rect fill="#1A1F71" width="60" height="24" rx="3"/>
                  <text x="30" y="17" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" fontStyle="italic">VISA</text>
                </svg>
              </div>
              <div className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5">
                <svg viewBox="0 0 60 24" className="h-5">
                  <rect fill="#000" width="60" height="24" rx="3"/>
                  <circle cx="22" cy="12" r="8" fill="#EB001B"/>
                  <circle cx="38" cy="12" r="8" fill="#F79E1B"/>
                </svg>
              </div>
              <div className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5">
                <svg viewBox="0 0 60 24" className="h-5">
                  <rect fill="#00A651" width="60" height="24" rx="3"/>
                  <text x="30" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">MIA</text>
                </svg>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm" style={{ color: COLORS.grayLight }}>
              {t('general.copyright')}. {t('landing.allRightsReserved')}.
            </p>
            <div className="flex items-center gap-6">
              <a href="https://t.me/PolyTechWallet" className="hover:text-green-500 transition" style={{ color: COLORS.grayLight }}>
                <Send size={20} />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
